/* @flow */
import * as L from 'partial.lenses';

import { type Belt, type Customer, type SimulationState, type Sushi, beltSlot } from './types';

function compactBelt(input: Belt): Belt {
  let result = input.filter(slot => slot.type === 'filled');
  // belt must have at least 6 slot for animation
  if (result.length < 6) {
    for (let i = result.length; i < 6; i++) {
      result.push(beltSlot.empty);
    }
  }
  return result;
}

export function satisfiedCustomers(customers: Customer[], state: SimulationState): Customer[] {
  let remainSushi = state.belt.map(slot => (slot.type === 'filled' ? slot.sushi : null)).filter(Boolean);
  return customers.filter((customer, i) => {
    let eaten = state.eatens[i];
    let order = customer.orders[eaten.length];

    // all orders are satisfied
    if (!order) return true;

    // there are no sushi satisfying current order
    return !remainSushi.some(sushi => order(sushi, eaten));
  });
}

function isEndState(customers: Customer[], state: SimulationState) {
  return satisfiedCustomers(customers, state).length === customers.length;
}

const INFINITE_LOOP_GUARD = 10000;
export function execute(customers: Array<Customer>, initialEatens: Array<Array<Sushi>>, initialBelt: Belt) {
  let belt = compactBelt(initialBelt);
  let customerCount = customers.length;
  let eatens = initialEatens;
  let states: Array<SimulationState> = [];

  let t;
  for (t = 0; t < INFINITE_LOOP_GUARD; t++) {
    for (let i = 0; i < customerCount; i++) {
      let customer = customers[i];
      let eaten = eatens[i];
      let order = customer.orders[eaten.length];

      let position = i;
      if (customerCount === 1) {
        // special: when there is only 1 customer, they take middle sit (position 1)
        position = 1;
      }
      let beltIndex = (t + position) % belt.length;
      let slot = belt[beltIndex];

      if (order && slot.type === 'filled' && order(slot.sushi, eaten)) {
        // add sushi to eatens
        eatens = L.set([i], eaten.concat([slot.sushi]), eatens);
        // set slot to empty
        belt = L.set([beltIndex], beltSlot.eaten(slot.sushi), belt);
      }
    }

    let state = { t, eatens, belt };
    states.push(state);
    if (isEndState(customers, state)) break;
  }

  if (t >= INFINITE_LOOP_GUARD) {
    throw new Error('Maximum number of loop reached');
  }

  return states;
}
